import React, { useEffect, useRef } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import Lottie, { type AnimationItem } from 'lottie-web';

import shapeMorphAnimation from './assets/shape-morph-animation.json';

const CONTAINER_STYLE: React.CSSProperties = {
	width: 24,
	height: 24,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	overflow: 'visible',
};

const LOTTIE_WRAPPER_STYLE: React.CSSProperties = {
	width: 16,
	height: 16,
};

type MorphingShapeLoaderProps = {
	testId?: string;
};

export const MorphingShapeLoader = ({ testId }: MorphingShapeLoaderProps): JSX.Element => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	// Use refs instead of module-level variables so each component instance
	// maintains its own animation state, scoped to its own lifecycle.
	const savedFrameRef = useRef(0);
	const hasPlayedBeforeRef = useRef(false);

	useEffect(() => {
		const currentRef = containerRef.current;
		if (!currentRef) {
			return;
		}

		const baseAnimationConfig = {
			container: currentRef,
			animationData: shapeMorphAnimation,
			renderer: 'svg' as const,
			loop: true,
		};

		let animationInstance: AnimationItem;

		if (hasPlayedBeforeRef.current) {
			// Remount — resume from saved frame for smooth animation continuity
			animationInstance = Lottie.loadAnimation({ ...baseAnimationConfig, autoplay: false });
			animationInstance.goToAndPlay(savedFrameRef.current, true);
		} else {
			// First mount — start from scratch
			animationInstance = Lottie.loadAnimation({ ...baseAnimationConfig, autoplay: true });
		}

		hasPlayedBeforeRef.current = true;

		return () => {
			// Save current frame before destroying so we can resume on remount
			savedFrameRef.current = animationInstance.currentFrame;
			animationInstance.destroy();
		};
	}, []);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- static styles for lottie container
		<div style={CONTAINER_STYLE} data-testid={testId}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- static styles for lottie wrapper */}
			<div style={LOTTIE_WRAPPER_STYLE} ref={containerRef} />
		</div>
	);
};
